<template>
	<div
		:class="[
			'c-Table',
			isLoading ? 'is-loading-table' : '',
			this.truncateRowHeaders ? 'is-truncated' : ''
		]"
	>
		<div class="c-Table__wrapper">
			<table class="c-Table__table">
				<thead class="c-Table__head">
					<tr>
						<th
							v-for="(column, index) in columns"
							:key="index"
						>
							<span> {{ column.label }}</span>
						</th>
					</tr>
				</thead>
				<tbody
					v-if="!isLoading"
					class="c-Table__body"
				>
					<template
						v-if="rows.length > 0"
					>
						<tr
							v-for="(row, index) in rows"
							:key="index"
						>
							<slot
								name="items"
								:row="row"
							/>
						</tr>
					</template>
					<template v-else>
						<tr>
							<td :colspan="columns.length">
								<slot name="no-data" />
							</td>
						</tr>
					</template>
				</tbody>
				<tbody
					v-else
					class="c-Table__body"
				>
					<tr
						v-for="(row, rowIndex) in paginationItems"
						:key="rowIndex"
					>
						<td
							v-for="(col, colIndex) in columns"
							:key="colIndex"
							class="is-loading-cell"
						>
							<span class="c-Table__loadingCell" />
						</td>
					</tr>
				</tbody>
			</table>
		</div>
		<Pagination
			v-if="hasPagination"
			:current-page="currentPage"
			:total-pages="totalPages"
			:is-loading="isLoading"
			@changePage="changePage"
		/>
	</div>
</template>

<script>
import Pagination from '../Pagination/Pagination.vue'

export default {
	name: 'Table',
	components: {
		Pagination
	},
	props: {
		/**
		 * An array of objects representing column headers
		 */
		columns: {
			type: Array,
			required: true,
			default: () => [],
		},
		/**
		 * An array of objects representing row data
		 */
		rows: {
			type: Array,
			required: true,
			default: () => [],
		},
		/**
		 * Determines whether table pagination is shown
		 */
		hasPagination: {
			type: Boolean,
			default: false,
		},
		/**
		 * The value (index) of the current page
		 */
		currentPage: {
			type: Number,
			default: 0,
		},
		/**
		 * The total number of available pages
		 */
		totalPages: {
			type: Number,
			default: 0,
		},
		/**
		 * The number of items
		 */
		paginationItems: {
			type: Number,
			default: 10,
		},
		/**
		 * Displays a skeleton loading animation within the cells
		 */
		isLoading: {
			type: Boolean,
			default: false,
		},
		/**
		 * Hides overflown text and adds an ellipsis
		 */
		truncateRowHeaders: {
			type: Boolean,
			default: false,
		},
		/**
		 * Table style variations
		 * @values no-border
		 */
		tableStyle: {
			type: String,
			required: false,
			default: () => '',
			validator (type) {
				return [
					'',
					'no-border',
				].includes(type)
			}
		},
	},
	methods: {
		changePage(page) {
			this.$emit('changePage', page);
		}
	},
};
</script>

<style lang="scss" scoped>
@import "../../scss/variables.scss";
@import "../../scss/mixins.scss";

$table-padding: 0 #{$base-padding + 8}; // 0 24px
$table-border-color: $un-gray1-dark;
$table-border: solid 1px $table-border-color;

.c-Table {
	--rowBackgroundColor: #{$un-blue-light};
	$parent: &;

	&.no-border {
		--tableBorder: none;
	}

	&.is-truncated {
		#{$parent}__body {
			th {
				text-overflow: ellipsis;
				white-space: nowrap;
				overflow: hidden;
				max-width: 200px;
			}
		}
	}

	&.is-loading-table {
		--rowBackgroundColor: transparent
	}

	&__wrapper,
	&__body {
		width: 100%;
	}

	&__table {
		border-collapse: collapse;
		background-color: $un-white;
		width: 100%;
	}
	&__head {
		border-bottom: $table-border;

		th {
			padding: $base-padding #{$base-padding + 8};
			text-align: left;
			font-weight: bold;
			color: $un-gray-textsec;
		}
	}

	&__body {
		th {
			font-weight: bold;
		}
		td, th {
			padding: $table-padding;
			vertical-align: middle;
			height: #{$base-size * 3};
			color: $un-gray-bd;

			&.is-loading-cell {
				span {
					display: block;
					border-radius: #{$border-radius * 2};
					height: $base-padding;
					@include skeleton-blink-loading;
				}
			}
		}
		tr {
			border-bottom: $table-border;
			background-color: transparent;
			transition: background-color .2s;

			a {
				color: $un-blue;
				text-decoration: none;
				:focus {
					outline: 3px solid red;
				}
			}

			&:hover {
				background-color: var(--rowBackgroundColor);
				transition: background-color .2s;

				a {
					text-decoration: underline;
				}
			}

			th {
				padding: $table-padding;
				text-align: left;
			}
		}
	}
}
</style>
